import React, { useState, useRef } from 'react';

import { WidgetProps } from './WidgetProps';
import { getBorderStyle, getDropShadowStyle, getWidgetOpacity } from './helper';
import { useWidgetAnimation } from './useWidgetAnimation';

const generateHotspotStyle = ({
	isShowWidget,
	isExiting,
	style,
	isHover,
	isClickable
}: {
	isShowWidget?: boolean;
	isExiting?: boolean;
	style: WidgetProps.StyleProps;
	isHover?: boolean;
	isClickable?: boolean;
}) => {
	const {
		transformSettings: {
			borderRadiusBottom,
			borderRadiusLeft,
			borderRadiusRight,
			borderRadiusTop,
			paddingBottom,
			paddingLeft,
			paddingRight,
			paddingTop,
			hoverPaddingBottom,
			hoverPaddingLeft,
			hoverPaddingRight,
			hoverPaddingTop,
		},
		backgroundSettings: {
			backgroundColor,
			hoverBackgroundColor,
		},
	} = style;

	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);

	return {
		display: isShowWidget || isExiting ? 'flex' : 'none',
		width: '100%',
		height: '100%',
		backgroundColor: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
		borderTopRightRadius: `${borderRadiusTop}px`,
		borderBottomRightRadius: `${borderRadiusRight}px`,
		borderBottomLeftRadius: `${borderRadiusBottom}px`,
		borderTopLeftRadius: `${borderRadiusLeft}px`,
		paddingBottom: isHover ? (hoverPaddingBottom ?? paddingBottom) : paddingBottom,
		paddingLeft: isHover ? (hoverPaddingLeft ?? paddingLeft) : paddingLeft,
		paddingRight: isHover ? (hoverPaddingRight ?? paddingRight) : paddingRight,
		paddingTop: isHover ? (hoverPaddingTop ?? paddingTop) : paddingTop,
		border: isHover ? (hoverBorder || border) : border,
	} as React.CSSProperties;
};

const generateWrapperStyle = ({
	style,
	isHover,
	isClickable,
}: {
	style: WidgetProps.StyleProps;
	isHover: boolean;
	isClickable?: boolean;
}) => {
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);
	return {
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
	} as React.CSSProperties;
};

const HotspotWidget = ({
	id,
	isShowWidget,
	isExiting,
	onExitDone,
	itemRef,
	onClickItem,
	widgetTemplateId,
	style,
	isSafari,
	onContextMenu,
	isClickable,
}: WidgetProps.BaseProps) => {

	const gElementRef = useRef<SVGGElement | null>();
	const bgElementRef = useRef<HTMLDivElement | null>();

	const [isHover, setIsHover] = useState(false);

	const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, isClickable });

	const handleMouseEnter = () => {
		setIsHover(true);
	};

	const handleMouseLeave = () => {
		setIsHover(false);
	};

	return (
		<g id={widgetTemplateId} className={`${!isSafari ? 'widget' : ''} widget_${widgetTemplateId}`} style={{ opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
			<g ref={itemRef}>
				<g
					ref={r => gElementRef.current = r}
					data-template-id={widgetTemplateId}
					data-widget-id={id}
					onClick={(e) => {
						e.stopPropagation();
						if (!isShowWidget) { return; }
						triggerClickAnimation(() => onClickItem?.(itemRef));
					}}
					onContextMenu={(e) => {
						e.preventDefault();
						onContextMenu?.(itemRef, !!isShowWidget);
						return false;
					}}
					style={generateWrapperStyle({ style, isHover, isClickable })}
				>
					<foreignObject>
						<div
							ref={r => bgElementRef.current = r}
							className="button-widget"
							style={generateHotspotStyle({ isShowWidget, isExiting, style, isHover, isClickable })}
							onMouseEnter={handleMouseEnter}
							onMouseLeave={handleMouseLeave}
						/>
					</foreignObject>
				</g>
			</g>
		</g>
	);
};

export default HotspotWidget;
